import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import {
  Col,
  Container,
  Input,
  Label,
  Row,
  Card,
  CardBody,
  Alert,
} from "reactstrap"
import axios from "axios"
import withRouter from "components/Common/withRouter"
import SnackbarComponent from "components/SnackBar"
import { useFormik } from "formik"
import * as Yup from "yup"
import "../../newcomponents/index.css"
import PrimaryButton from "newcomponents/primary-button"
import { ArrowForward } from "@mui/icons-material"
import PasswordIcon from "@mui/icons-material/Password"
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import { Link } from "react-router-dom"
import config from "config/config"
import { IntroScreen } from "./intro-screen"

const ResetPasswordPage = props => {
  document.title = "Reset Password | Reputation Management"
  const [err, setErr] = useState("")
  const [success, setSuccess] = useState("")
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [tokenValid, setTokenValid] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDone, setIsDone] = useState(false)

  const token = new URLSearchParams(props.router.location.search).get("token")

  useEffect(() => {
    if (!token) {
      setTokenValid(false)
    }
  }, [token])

  const validation = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Please enter your new password"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
        .required("Please confirm your password"),
    }),
    onSubmit: async values => {
      if (!token) {
        setErr("Invalid or missing reset token.")
        return
      }

      setIsSubmitting(true)
      setErr("")
      setSuccess("")

      try {
        await axios.post(`${config.backendUrl}/auth/reset-password`, {
          token,
          newPassword: values.newPassword,
        })

        setSuccess("Password reset successful! Redirecting to login...")
        setIsDone(true)
        setTimeout(() => {
          props.router.navigate("/login")
        }, 2000)
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          "Failed to reset password. The link may have expired."
        setErr(message)
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  return (
    <React.Fragment>
      <SnackbarComponent
        err={err}
        success={success}
        setErr={setErr}
        setSuccess={setSuccess}
      />

      <Container
        fluid
        className="min-vh-100 d-flex flex-column primary-bg"
        style={{
          fontFamily: "Inter",
          backgroundImage: `url('/circle1.svg'), url('/circle2.svg')`,
          backgroundPosition: "bottom left, top right",
          backgroundRepeat: "no-repeat, no-repeat",
          backgroundSize: "24%, 24%",
        }}
      >
        <Row className="flex-grow-1">
          {/* Intro panel (desktop only) */}
          <Col lg={6} className="d-none d-lg-flex align-items-center">
            <IntroScreen
              buttonText="Sign In"
              onClick={() => props.router.navigate("/login")}
            />
          </Col>

          {/* Form */}
          <Col
            lg={6}
            className="d-flex align-items-center justify-content-center"
            style={{ padding: "40px" }}
          >
            <div className="w-100" style={{ maxWidth: "470px" }}>
              <Card
                style={{ borderRadius: "20px", padding: "20px 0px" }}
                className="shadow-sm border-0"
              >
                <CardBody>
                  <div className="text-center mb-4">
                    <h2
                      style={{
                        fontSize: "32px",
                        fontWeight: 400,
                        color: "#050409",
                        fontFamily: "Inter",
                      }}
                    >
                      Reset <span style={{ fontWeight: 700 }}>Password</span>
                    </h2>
                    <p className="text-muted mb-0">
                      Enter your new password below.
                    </p>
                  </div>

                  {!tokenValid && (
                    <Alert color="danger">
                      This reset link is invalid or missing. Please{" "}
                      <Link to="/forgot-password" className="alert-link">
                        request a new one
                      </Link>
                      .
                    </Alert>
                  )}

                  {err && <Alert color="danger">{err}</Alert>}
                  {success && <Alert color="success">{success}</Alert>}

                  {tokenValid && !isDone && (
                    <form
                      className="mt-4"
                      onSubmit={e => {
                        e.preventDefault()
                        validation.handleSubmit()
                        return false
                      }}
                    >
                      {/* New Password */}
                      <div className="mb-3">
                        <Label
                          htmlFor="newPassword"
                          className="text-uppercase"
                          style={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#050409",
                            fontFamily: "Inter",
                          }}
                        >
                          New Password
                        </Label>
                        <div
                          className="position-relative d-flex align-items-center"
                          style={{
                            padding: "8px",
                            border:
                              validation.touched.newPassword &&
                              validation.errors.newPassword
                                ? "1px solid #ef4444"
                                : "1px solid #d1d5db",
                            borderRadius: "8px",
                          }}
                        >
                          <PasswordIcon
                            style={{ color: "#2990DF", marginLeft: "8px" }}
                          />
                          <Input
                            id="newPassword"
                            name="newPassword"
                            type={showNewPassword ? "text" : "password"}
                            placeholder="Enter new password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.newPassword}
                            style={{
                              border: "none",
                              boxShadow: "none",
                              fontFamily: "Inter",
                              marginLeft: "8px",
                            }}
                          />
                          <span
                            style={{
                              position: "absolute",
                              right: "12px",
                              cursor: "pointer",
                              color: "#6b7280",
                            }}
                            onClick={() =>
                              setShowNewPassword(!showNewPassword)
                            }
                          >
                            {showNewPassword ? (
                              <VisibilityOutlinedIcon />
                            ) : (
                              <VisibilityOffOutlinedIcon />
                            )}
                          </span>
                        </div>
                        {validation.touched.newPassword &&
                          validation.errors.newPassword && (
                            <div
                              style={{
                                color: "#ef4444",
                                fontSize: "13px",
                                marginTop: "4px",
                              }}
                            >
                              {validation.errors.newPassword}
                            </div>
                          )}
                      </div>

                      {/* Confirm Password */}
                      <div className="mb-3">
                        <Label
                          htmlFor="confirmPassword"
                          className="text-uppercase"
                          style={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#050409",
                            fontFamily: "Inter",
                          }}
                        >
                          Confirm Password
                        </Label>
                        <div
                          className="position-relative d-flex align-items-center"
                          style={{
                            padding: "8px",
                            border:
                              validation.touched.confirmPassword &&
                              validation.errors.confirmPassword
                                ? "1px solid #ef4444"
                                : "1px solid #d1d5db",
                            borderRadius: "8px",
                          }}
                        >
                          <PasswordIcon
                            style={{ color: "#2990DF", marginLeft: "8px" }}
                          />
                          <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm new password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.confirmPassword}
                            style={{
                              border: "none",
                              boxShadow: "none",
                              fontFamily: "Inter",
                              marginLeft: "8px",
                            }}
                          />
                          <span
                            style={{
                              position: "absolute",
                              right: "12px",
                              cursor: "pointer",
                              color: "#6b7280",
                            }}
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          >
                            {showConfirmPassword ? (
                              <VisibilityOutlinedIcon />
                            ) : (
                              <VisibilityOffOutlinedIcon />
                            )}
                          </span>
                        </div>
                        {validation.touched.confirmPassword &&
                          validation.errors.confirmPassword && (
                            <div
                              style={{
                                color: "#ef4444",
                                fontSize: "13px",
                                marginTop: "4px",
                              }}
                            >
                              {validation.errors.confirmPassword}
                            </div>
                          )}
                      </div>

                      <div className="d-flex justify-content-center mt-4">
                        <PrimaryButton
                          type="submit"
                          style={{ padding: "15px 30px" }}
                          icon={<ArrowForward />}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Resetting..." : "Set New Password"}
                        </PrimaryButton>
                      </div>
                    </form>
                  )}

                  <div className="text-center mt-4">
                    <p className="inter secondary-color">
                      Remember it?{" "}
                      <Link
                        to="/login"
                        className="primary-color underline-link inter semibold-font"
                      >
                        Sign In
                      </Link>
                    </p>
                  </div>
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  )
}

ResetPasswordPage.propTypes = {
  router: PropTypes.object,
}

export default withRouter(ResetPasswordPage)
